import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { TagIcon, UserIcon, BuildingOfficeIcon, CheckIcon } from '@heroicons/react/24/outline';
import { Form, Button, Alert, Row, Col } from 'react-bootstrap';

const Register: React.FC = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    userType: 'individual',
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phone: '',
    companyName: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    const result = await register({
      username: formData.username,
      email: formData.email,
      password: formData.password,
      firstName: formData.firstName,
      lastName: formData.lastName,
      phone: formData.phone,
      userType: formData.userType,
      companyName: formData.userType === 'company' ? formData.companyName : undefined,
    });

    setLoading(false);

    if (result.success) {
      navigate(`/verify?type=phone&email=${formData.email}`);
    } else {
      setError(result.message);
    }
  };

  const nextStep = () => {
    if (step === 1) {
      if (!formData.userType) {
        setError('Please select account type');
        return;
      }
    }
    setError('');
    setStep(step + 1);
  };

  const prevStep = () => {
    setError('');
    setStep(step - 1);
  };

  return (
    <Layout showSidebar={false}>
      <div className="d-flex align-items-center justify-content-center py-5 px-4 pw-page" style={{ minHeight: '60vh' }}>
        <div className="w-100" style={{ maxWidth: '512px' }}>
          <div className="bg-white rounded-4 border p-4 p-md-5 d-none d-md-block pw-card">
            <div className="text-center mb-4">
              <div className="d-md-none mx-auto mb-3 rounded-3 d-flex align-items-center justify-content-center shadow-primary" style={{ width: '48px', height: '48px', background: 'linear-gradient(135deg, #0d6efd, #084298)' }}>
                <TagIcon className="text-white" style={{ width: '24px', height: '24px' }} />
              </div>
              <h1 className="fw-bold text-dark fs-4 fs-md-3 mb-2">Create Account</h1>
              <p className="text-secondary">Join NayaBajar today</p>
            </div>

            {/* Progress Steps */}
            <div className="d-flex justify-content-center mb-4">
              {[1, 2, 3].map((s) => (
                <div key={s} className="d-flex align-items-center">
                  <div
                    className={`rounded-circle d-flex align-items-center justify-content-center fw-semibold ${
                      s < step
                        ? 'bg-success text-white'
                        : s === step
                        ? 'bg-primary text-white'
                        : 'bg-light text-secondary'
                    }`}
                    style={{ width: step < 2 ? '32px' : '40px', height: step < 2 ? '32px' : '40px', fontSize: '14px' }}
                  >
                    {s < step ? <CheckIcon style={{ width: '20px', height: '20px' }} /> : s}
                  </div>
                  {s < 3 && (
                    <div
                      className={`transition-colors`}
                      style={{ width: '48px', height: '4px', backgroundColor: s < step ? '#198754' : '#e9ecef' }}
                    />
                  )}
                </div>
              ))}
            </div>

            {error && (
              <Alert variant="danger" className="mb-3">
                {error}
              </Alert>
            )}

            <Form onSubmit={handleSubmit}>
              {step === 1 && (
                <div className="d-flex flex-column gap-4">
                  <h2 className="fs-5 fs-md-4 fw-semibold text-center text-dark">Choose Account Type</h2>
                  <div className="row g-3">
                    <div className="col-6">
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, userType: 'individual' })}
                        className={`w-100 p-3 p-md-4 border-2 rounded-3 text-center transition-all ${
                          formData.userType === 'individual'
                            ? 'border-primary bg-primary bg-opacity-10'
                            : 'border hover:border-secondary'
                        }`}
                      >
                        <div className="mx-auto mb-2 rounded-circle d-flex align-items-center justify-content-center bg-primary bg-opacity-10" style={{ width: '48px', height: '48px' }}>
                          <UserIcon className="text-primary" style={{ width: '24px', height: '24px' }} />
                        </div>
                        <div className="fw-semibold text-dark">Individual</div>
                        <div className="text-secondary" style={{ fontSize: '14px' }}>For personal use</div>
                      </button>
                    </div>
                    <div className="col-6">
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, userType: 'company' })}
                        className={`w-100 p-3 p-md-4 border-2 rounded-3 text-center transition-all ${
                          formData.userType === 'company'
                            ? 'border-primary bg-primary bg-opacity-10'
                            : 'border hover:border-secondary'
                        }`}
                      >
                        <div className="mx-auto mb-2 rounded-circle d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', backgroundColor: 'rgba(245, 158, 11, 0.1)' }}>
                          <BuildingOfficeIcon style={{ width: '24px', height: '24px', color: '#f59e0b' }} />
                        </div>
                        <div className="fw-semibold text-dark">Company</div>
                        <div className="text-secondary" style={{ fontSize: '14px' }}>For business use</div>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="d-flex flex-column gap-3">
                  <h2 className="fs-5 fs-md-4 fw-semibold text-center text-dark">Account Information</h2>
                  <Row className="g-3">
                    <Col xs={6}>
                      <Form.Label className="fw-medium text-secondary small">
                        First Name
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.firstName}
                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                        className="px-3 py-2 border rounded-3"
                        required
                      />
                    </Col>
                    <Col xs={6}>
                      <Form.Label className="fw-medium text-secondary small">
                        Last Name
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.lastName}
                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                        className="px-3 py-2 border rounded-3"
                        required
                      />
                    </Col>
                  </Row>
                  <Form.Group>
                    <Form.Label className="fw-medium text-secondary small">
                      Username
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.username}
                      onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                      className="px-3 py-2 border rounded-3"
                      required
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label className="fw-medium text-secondary small">
                      Email
                    </Form.Label>
                    <Form.Control
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="px-3 py-2 border rounded-3"
                      required
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label className="fw-medium text-secondary small">
                      Phone Number
                    </Form.Label>
                    <Form.Control
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="px-3 py-2 border rounded-3"
                      placeholder="+977XXXXXXXXXX"
                      required
                    />
                  </Form.Group>
                  {formData.userType === 'company' && (
                    <Form.Group>
                      <Form.Label className="fw-medium text-secondary small">
                        Company Name
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.companyName}
                        onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                        className="px-3 py-2 border rounded-3"
                        required
                      />
                    </Form.Group>
                  )}
                </div>
              )}

              {step === 3 && (
                <div className="d-flex flex-column gap-3">
                  <h2 className="fs-5 fs-md-4 fw-semibold text-center text-dark">Set Password</h2>
                  <Form.Group>
                    <Form.Label className="fw-medium text-secondary small">
                      Password
                    </Form.Label>
                    <Form.Control
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="px-3 py-2 border rounded-3"
                      required
                      minLength={8}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label className="fw-medium text-secondary small">
                      Confirm Password
                    </Form.Label>
                    <Form.Control
                      type="password"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      className="px-3 py-2 border rounded-3"
                      required
                    />
                  </Form.Group>
                  <Form.Check
                    type="checkbox"
                    className="d-flex align-items-start"
                    required
                    label={
                      <span className="text-secondary small ms-2">
                        I agree to the{' '}
                        <Link to="/terms" className="text-primary text-decoration-none">
                          Terms of Service
                        </Link>{' '}
                        and{' '}
                        <Link to="/privacy" className="text-primary text-decoration-none">
                          Privacy Policy
                        </Link>
                      </span>
                    }
                  />
                </div>
              )}

              <div className="d-flex justify-content-between mt-4">
                {step > 1 && (
                  <Button
                    variant="secondary"
                    type="button"
                    onClick={prevStep}
                  >
                    Back
                  </Button>
                )}
                {step < 3 ? (
                  <Button
                    variant="primary"
                    type="button"
                    onClick={nextStep}
                    className="ms-auto"
                  >
                    Next
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={loading}
                    className="ms-auto"
                    style={{ opacity: loading ? 0.5 : 1 }}
                  >
                    {loading ? (
                      <span className="d-flex align-items-center">
                        <svg className="animate-spin me-2" style={{ width: '20px', height: '20px', color: 'white' }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Creating...
                      </span>
                    ) : (
                      'Create Account'
                    )}
                  </Button>
                )}
              </div>
            </Form>

            <div className="mt-4 text-center">
              <p className="text-secondary">
                Already have an account?{' '}
                <Link to="/login" className="text-primary fw-semibold text-decoration-none">
                  Sign in
                </Link>
              </p>
            </div>
          </div>

          {/* Mobile Version */}
          <div className="bg-white p-4 d-md-none pw-card">
            <div className="text-center mb-4">
              <div className="d-md-none mx-auto mb-3 rounded-3 d-flex align-items-center justify-content-center shadow-primary" style={{ width: '48px', height: '48px', background: 'linear-gradient(135deg, #0d6efd, #084298)' }}>
                <TagIcon className="text-white" style={{ width: '24px', height: '24px' }} />
              </div>
              <h1 className="fw-bold text-dark fs-4 mb-2">Create Account</h1>
              <p className="text-secondary">Join NayaBajar today</p>
            </div>

            {/* Progress Steps */}
            <div className="d-flex justify-content-center mb-4">
              {[1, 2, 3].map((s) => (
                <div key={s} className="d-flex align-items-center">
                  <div
                    className={`rounded-circle d-flex align-items-center justify-content-center fw-semibold ${
                      s < step
                        ? 'bg-success text-white'
                        : s === step
                        ? 'bg-primary text-white'
                        : 'bg-light text-secondary'
                    }`}
                    style={{ width: step < 2 ? '32px' : '40px', height: step < 2 ? '32px' : '40px', fontSize: '14px' }}
                  >
                    {s < step ? <CheckIcon style={{ width: '20px', height: '20px' }} /> : s}
                  </div>
                  {s < 3 && (
                    <div
                      className={`transition-colors`}
                      style={{ width: '48px', height: '4px', backgroundColor: s < step ? '#198754' : '#e9ecef' }}
                    />
                  )}
                </div>
              ))}
            </div>

            {error && (
              <Alert variant="danger" className="mb-3">
                {error}
              </Alert>
            )}

            <Form onSubmit={handleSubmit}>
              {step === 1 && (
                <div className="d-flex flex-column gap-4">
                  <h2 className="fs-5 fw-semibold text-center text-dark">Choose Account Type</h2>
                  <div className="row g-3">
                    <div className="col-6">
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, userType: 'individual' })}
                        className={`w-100 p-3 border-2 rounded-3 text-center transition-all ${
                          formData.userType === 'individual'
                            ? 'border-primary bg-primary bg-opacity-10'
                            : 'border hover:border-secondary'
                        }`}
                      >
                        <div className="mx-auto mb-2 rounded-circle d-flex align-items-center justify-content-center bg-primary bg-opacity-10" style={{ width: '48px', height: '48px' }}>
                          <UserIcon className="text-primary" style={{ width: '24px', height: '24px' }} />
                        </div>
                        <div className="fw-semibold text-dark">Individual</div>
                        <div className="text-secondary" style={{ fontSize: '14px' }}>For personal use</div>
                      </button>
                    </div>
                    <div className="col-6">
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, userType: 'company' })}
                        className={`w-100 p-3 border-2 rounded-3 text-center transition-all ${
                          formData.userType === 'company'
                            ? 'border-primary bg-primary bg-opacity-10'
                            : 'border hover:border-secondary'
                        }`}
                      >
                        <div className="mx-auto mb-2 rounded-circle d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', backgroundColor: 'rgba(245, 158, 11, 0.1)' }}>
                          <BuildingOfficeIcon style={{ width: '24px', height: '24px', color: '#f59e0b' }} />
                        </div>
                        <div className="fw-semibold text-dark">Company</div>
                        <div className="text-secondary" style={{ fontSize: '14px' }}>For business use</div>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="d-flex flex-column gap-3">
                  <h2 className="fs-5 fw-semibold text-center text-dark">Account Information</h2>
                  <div className="row g-3">
                    <div className="col-6">
                      <Form.Label className="fw-medium text-secondary small">
                        First Name
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.firstName}
                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                        className="px-3 py-2 border rounded-3"
                        required
                      />
                    </div>
                    <div className="col-6">
                      <Form.Label className="fw-medium text-secondary small">
                        Last Name
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.lastName}
                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                        className="px-3 py-2 border rounded-3"
                        required
                      />
                    </div>
                  </div>
                  <Form.Group>
                    <Form.Label className="fw-medium text-secondary small">
                      Username
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.username}
                      onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                      className="px-3 py-2 border rounded-3"
                      required
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label className="fw-medium text-secondary small">
                      Email
                    </Form.Label>
                    <Form.Control
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="px-3 py-2 border rounded-3"
                      required
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label className="fw-medium text-secondary small">
                      Phone Number
                    </Form.Label>
                    <Form.Control
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="px-3 py-2 border rounded-3"
                      placeholder="+977XXXXXXXXXX"
                      required
                    />
                  </Form.Group>
                  {formData.userType === 'company' && (
                    <Form.Group>
                      <Form.Label className="fw-medium text-secondary small">
                        Company Name
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.companyName}
                        onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                        className="px-3 py-2 border rounded-3"
                        required
                      />
                    </Form.Group>
                  )}
                </div>
              )}

              {step === 3 && (
                <div className="d-flex flex-column gap-3">
                  <h2 className="fs-5 fw-semibold text-center text-dark">Set Password</h2>
                  <Form.Group>
                    <Form.Label className="fw-medium text-secondary small">
                      Password
                    </Form.Label>
                    <Form.Control
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="px-3 py-2 border rounded-3"
                      required
                      minLength={8}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label className="fw-medium text-secondary small">
                      Confirm Password
                    </Form.Label>
                    <Form.Control
                      type="password"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      className="px-3 py-2 border rounded-3"
                      required
                    />
                  </Form.Group>
                  <Form.Check
                    type="checkbox"
                    className="d-flex align-items-start"
                    required
                    label={
                      <span className="text-secondary small ms-2">
                        I agree to the{' '}
                        <Link to="/terms" className="text-primary text-decoration-none">
                          Terms of Service
                        </Link>{' '}
                        and{' '}
                        <Link to="/privacy" className="text-primary text-decoration-none">
                          Privacy Policy
                        </Link>
                      </span>
                    }
                  />
                </div>
              )}

              <div className="d-flex justify-content-between mt-4">
                {step > 1 && (
                  <Button
                    variant="secondary"
                    type="button"
                    onClick={prevStep}
                  >
                    Back
                  </Button>
                )}
                {step < 3 ? (
                  <Button
                    variant="primary"
                    type="button"
                    onClick={nextStep}
                    className="ms-auto"
                  >
                    Next
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={loading}
                    className="ms-auto"
                    style={{ opacity: loading ? 0.5 : 1 }}
                  >
                    {loading ? (
                      <span className="d-flex align-items-center">
                        <svg className="animate-spin me-2" style={{ width: '20px', height: '20px', color: 'white' }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Creating...
                      </span>
                    ) : (
                      'Create Account'
                    )}
                  </Button>
                )}
              </div>
            </Form>

            <div className="mt-4 text-center">
              <p className="text-secondary">
                Already have an account?{' '}
                <Link to="/login" className="text-primary fw-semibold text-decoration-none">
                  Sign in
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Register;
